import { collectOrgIdsForAnyOrgPermission, requireAnySystemPermission, walkOrganizations, } from '../../../../backend/features/permissions/shared';
import { AuthError } from '@juvare/platform-app/App';
export function getAccountPermissions(config, userState, accountId, accountData) {
    if (userState) {
        const orgIdsFull = collectOrgIdsForAnyOrgPermission(userState, 'manage-members');
        const orgIdsTransfer = collectOrgIdsForAnyOrgPermission(userState, 'view-and-transfer-members', 'manage-members');
        const orgIdsUpdateProfilePicture = collectOrgIdsForAnyOrgPermission(userState, 'manage-profile-picture');
        const allowUpdateOrgMemberProfilePicture = !!accountData &&
            isAccountMemberOfOrganization(accountData, orgIdsFull) &&
            isAccountMemberOfOrganization(accountData, orgIdsUpdateProfilePicture);
        if (requireAnySystemPermission(userState, 'manage-accounts')) {
            return {
                allowCreate: true,
                allowUpdateFull: !!accountId,
                allowUpdateTransfer: !!accountId,
                allowUpdateOrg: !!accountId,
                allowDelete: userState.id !== accountId,
                allowUpdateUserProfilePicture: requireAnySystemPermission(userState, 'manage-profile-picture') || allowUpdateOrgMemberProfilePicture,
                mapData(data, prevData) {
                    checkAllowedSystemRole(userState.systemRole, data.systemRole, prevData === null || prevData === void 0 ? void 0 : prevData.systemRole, config.systemRoleMap);
                    return data;
                },
            };
        }
        const allowCreate = config.allowOrgAdminsToCreateAccounts && orgIdsFull.length > 0;
        const allowUpdateFull = userState.id === accountId || (!!accountData && isAccountMemberOfOrganization(accountData, orgIdsFull));
        const allowUpdateTransfer = !!accountData && isAccountMemberOfOrganization(accountData, orgIdsTransfer);
        const allowUpdateOrganization = !!accountData && requireAnySystemPermission(userState, 'manage-accounts-organizations');
        const allowDelete = userState.id !== accountId && allowUpdateFull;
        const allowUpdateUserProfilePicture = (userState.id === accountId && requireAnySystemPermission(userState, 'manage-profile-picture')) ||
            allowUpdateOrgMemberProfilePicture;
        return {
            allowCreate: allowCreate,
            allowUpdateFull: allowUpdateFull,
            allowUpdateTransfer: allowUpdateTransfer,
            allowUpdateOrg: allowUpdateOrganization,
            allowDelete: allowDelete,
            allowUpdateUserProfilePicture: allowUpdateUserProfilePicture,
            mapData: (data, prevData) => {
                if (prevData && !allowUpdateFull) {
                    if (allowUpdateOrganization) {
                        return mapDataForUpdateOrg(orgIdsFull, data, prevData);
                    }
                    return mapDataForOrgTransfer(orgIdsTransfer, data, prevData);
                }
                else {
                    return mapDataForSelfOrOrgAdmin(userState.systemRole, config.systemRoleMap, orgIdsFull, data, prevData);
                }
            },
        };
    }
    return unauthorized;
}
export function calculateOrgPermissions(context, accountData) {
    const orgRoleMap = context.appState.settings.orgRoleMap;
    const organizations = context.appState.organizations;
    return getOrgPermissions(orgRoleMap, organizations, Object.entries(accountData.orgRole));
}
export function calculateOrgPermissionsFromAccountColumns(context, accountColumns) {
    const orgRoleMap = context.appState.settings.orgRoleMap;
    const organizations = context.appState.organizations;
    const orgRoles = accountColumns.orgRole.map((role) => role.split('_'));
    return getOrgPermissions(orgRoleMap, organizations, orgRoles);
}
function getOrgPermissions(orgRoleMap, organizations, accountOrgRoles) {
    var _a, _b;
    const orgPermissions = {};
    for (const [orgId, orgRole] of accountOrgRoles) {
        // Skip roles that reference organizations that no longer exist in the network
        if (!(organizations === null || organizations === void 0 ? void 0 : organizations[orgId])) {
            continue;
        }
        const permissions = (_a = orgRoleMap[orgRole]) === null || _a === void 0 ? void 0 : _a.permissions;
        if (permissions) {
            orgPermissions[orgId] = [...new Set([...(orgPermissions[orgId] || []), ...permissions])];
        }
        if ((_b = orgRoleMap[orgRole]) === null || _b === void 0 ? void 0 : _b.inherit) {
            const childIds = walkOrganizations(orgId, organizations);
            childIds.forEach((id) => {
                orgPermissions[id] = [...new Set([...(orgPermissions[id] || []), ...permissions])];
            });
        }
    }
    return orgPermissions;
}
function mapDataForOrgTransfer(orgIds, data, prevData) {
    const orgRole = buildDataMapUsingAllowedOrgIds(orgIds, data.orgRole, prevData.orgRole);
    for (const orgId of orgIds) {
        if (orgRole[orgId]) {
            // cannot change role
            orgRole[orgId] = prevData.orgRole[orgId];
        }
        else {
            // can remove role
        }
    }
    const orgStatus = buildDataMapUsingAllowedOrgIds(orgIds, data.orgStatus, prevData.orgStatus);
    for (const orgId of orgIds) {
        if (orgStatus[orgId]) {
            if (prevData.orgStatus[orgId]) {
                // cannot change status
            }
            else {
                // can set status to pending
                orgStatus[orgId] = 'pending';
            }
        }
        else {
            // can remove status
        }
    }
    return Object.assign(Object.assign({}, prevData), { orgRole: orgRole, orgStatus: orgStatus });
}
function mapDataForUpdateOrg(orgIds, data, prevData) {
    const orgRole = buildDataMapUsingAllowedOrgIds(orgIds, data.orgRole, prevData.orgRole);
    const orgStatus = buildDataMapUsingAllowedOrgIds(orgIds, data.orgStatus, prevData.orgStatus);
    return Object.assign(Object.assign({}, prevData), { orgRole: orgRole, orgStatus: orgStatus });
}
function mapDataForSelfOrOrgAdmin(userStateRoleKey, systemRoleMap, orgIds, data, prevData) {
    const orgRole = buildDataMapUsingAllowedOrgIds(orgIds, data.orgRole, prevData === null || prevData === void 0 ? void 0 : prevData.orgRole);
    const orgStatus = buildDataMapUsingAllowedOrgIds(orgIds, data.orgStatus, prevData === null || prevData === void 0 ? void 0 : prevData.orgStatus);
    for (const [orgId, status] of Object.entries(data.orgStatus)) {
        if (status === 'pending' || status === 'withdrawn') {
            orgStatus[orgId] = status; // anybody can request/withdraw membership as long as they know the organization id
        }
        if (status === 'withdrawn' && orgRole[orgId]) {
            delete orgRole[orgId]; // when withdrawing remove org role
        }
    }
    checkAllowedSystemRole(userStateRoleKey, data.systemRole, prevData === null || prevData === void 0 ? void 0 : prevData.systemRole, systemRoleMap);
    return Object.assign(Object.assign({}, data), { systemRole: data.systemRole, orgRole: orgRole, orgStatus: orgStatus });
}
/**
 * Check if Account is member of Organization - at least one organization within the array is assigned to Account
 *
 * First checks `orgRole` and only if not found, then `orgStatus`
 *
 *
 * Warning:
 * * Loose check by default - orgStatus value is not being checked, considered a member even with `researching/pending/rejected/withdrawn` statuses
 * * Use `options` param for making this stricter
 */
export function isAccountMemberOfOrganization(accountData, orgIds, options) {
    for (const orgId of Object.keys(accountData.orgRole)) {
        if (orgIds.includes(orgId)) {
            return true;
        }
    }
    if (options === null || options === void 0 ? void 0 : options.skipOrgStatusCheck) {
        return false;
    }
    for (const orgId of Object.keys(accountData.orgStatus)) {
        if (orgIds.includes(orgId)) {
            return true;
        }
    }
    return false;
}
function buildDataMapUsingAllowedOrgIds(allowedOrgIds, dataMap, prevDataMap) {
    const newDataMap = {};
    for (const allowedOrgId of allowedOrgIds) {
        if (dataMap[allowedOrgId]) {
            newDataMap[allowedOrgId] = dataMap[allowedOrgId];
        }
    }
    if (prevDataMap) {
        for (const [orgId, value] of Object.entries(prevDataMap)) {
            if (!allowedOrgIds.includes(orgId)) {
                newDataMap[orgId] = value;
            }
        }
    }
    return newDataMap;
}
export function checkAllowedSystemRole(userStateRoleKey, wantedRoleKey, currentRoleKey, systemRoles) {
    if (currentRoleKey === wantedRoleKey) {
        return;
    }
    const userStateRole = systemRoles[userStateRoleKey];
    const wantedRole = systemRoles[wantedRoleKey];
    const currentRole = currentRoleKey && systemRoles[currentRoleKey];
    if (wantedRole.level > userStateRole.level || (currentRole && currentRole.level > userStateRole.level)) {
        throw new AuthError(userStateRole.label + ' cannot set system role to ' + wantedRole.label);
    }
}
export const hasLowerSystemRole = (userStateRoleKey, wantedRoleKey, systemRoles) => {
    const userStateRole = systemRoles[userStateRoleKey];
    const wantedRole = systemRoles[wantedRoleKey];
    return wantedRole.level > userStateRole.level;
};
const unauthorized = {
    allowCreate: false,
    allowUpdateFull: false,
    allowUpdateTransfer: false,
    allowUpdateOrg: false,
    allowDelete: false,
    allowUpdateUserProfilePicture: false,
    mapData: () => {
        throw new Error('Unauthorized');
    },
};
